import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Map, List, ChevronLeft, Star, MessageSquareText } from "lucide-react";
import { Header } from "@/components/Header";
import SearchBar, { type SearchFilters } from "@/components/SearchBar";
import { FilterChips } from "@/components/FilterChips";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Footer } from "@/components/Footer";
import { ServiceCard, type ServiceCardItem } from "@/components/ServiceCard";
import { useBuscar } from "@/lib/useBuscar";

function toYmd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const serviceOptions = [
  { id: 1, nome: "hospedagem" },
  { id: 2, nome: "creche" },
  { id: 3, nome: "passeio" },
  { id: 4, nome: "petsitter" },
  { id: 5, nome: "taxi_dog" },
  { id: 6, nome: "Adestramento" },
  { id: 7, nome: "Veterinário em Casa" },
  { id: 8, nome: "Banho e Tosa" },
];

function RatingStars({ value, size = "sm" }: { value: number; size?: "sm" | "md" }) {
  const nota = Math.max(0, Math.min(5, Math.round(value || 0)));
  const iconSize = size === "md" ? "h-5 w-5" : "h-4 w-4";

  return (
    <div className="flex items-center gap-0.5" aria-label={`Nota ${value} de 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${iconSize} ${
            star <= nota ? "fill-[#d4af37] text-[#d4af37]" : "text-slate-300"
          }`}
          strokeWidth={1.6}
        />
      ))}
    </div>
  );
}

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [visibleCount, setVisibleCount] = useState(8);
  const [cidades, setCidades] = useState<string[]>([]);
  const [reviewsItem, setReviewsItem] = useState<ServiceCardItem | null>(null);

  const cidade = searchParams.get("cidade") || "";
  const service = searchParams.get("service") || "";
  const tipo_servico_id_legacy = searchParams.get("tipo_servico_id") || "";
  const entrada = searchParams.get("entrada") || "";
  const saida = searchParams.get("saida") || "";
  const quantidade_pets = searchParams.get("quantidade_pets") || "";

  const serviceSlugToId: Record<string, number> = {
    hospedagem: 1,
    creche: 2,
    passeio: 3,
    petsitter: 4,
    taxi_dog: 5,
    "táxi dog": 5,
    taxidog: 5,
    adestramento: 6,
    veterinario: 7,
    "veterinário": 7,
    veterinario_casa: 7,
    "veterinário em casa": 7,
    "veterinario em casa": 7,
    banho_e_tosa: 8,
    "banho e tosa": 8,
    banhoetosa: 8,
    "banho-e-tosa": 8,
  };

  const queryString = useMemo(() => {
    const today = new Date();
    const entradaFinal = entrada || toYmd(today);
    const saidaFinal = saida || entradaFinal;

    const qs = new URLSearchParams();

    if (service) {
      if (/^\d+$/.test(service)) {
        qs.set("tipo_servico_id", service);
      } else {
        const mappedId = serviceSlugToId[service.toLowerCase()];
        if (mappedId) qs.set("tipo_servico_id", String(mappedId));
       else {
          console.warn("service inválido:", service);
          return "";
        }
      }

    } else if (tipo_servico_id_legacy) {
      qs.set("tipo_servico_id", tipo_servico_id_legacy);
    } else {
      qs.set("tipo_servico_id", "1"); // default hospedagem
    }

    qs.set("entrada", entradaFinal);
    qs.set("saida", saidaFinal);

    const cidadeFinal = (cidade || "").split(",")[0].trim();
    if (cidadeFinal) qs.set("cidade", cidadeFinal);
    if (quantidade_pets) qs.set("quantidade_pets", quantidade_pets);

    return qs.toString();
  }, [cidade, service, tipo_servico_id_legacy, entrada, saida, quantidade_pets]);

  const { items, total, loading, err } = useBuscar(queryString);

  useEffect(() => {
    fetch("/api/buscar/cidades")
      .then((r) => r.json())
      .then((json) => {
        const items = Array.isArray(json?.items) ? json.items : [];
        setCidades(
          items
            .map((item: any) => item.label || item.cidade)
            .filter(Boolean)
        );
      })
      .catch(() => {
        setCidades([]);
      });
  }, []);

  useEffect(() => {
    setVisibleCount(8);
  }, [queryString, sortBy, selectedFilters]);

  const handleSearch = (filters: SearchFilters) => {
    const params = new URLSearchParams(searchParams.toString());

    const cidadeSelecionada =
      (filters as any).cidade ||
      filters.cidade ||
      "";

    if (cidadeSelecionada) params.set("cidade", cidadeSelecionada);
    else params.delete("cidade");

    if (filters.dateRange?.from) {
      params.set("entrada", toYmd(filters.dateRange.from));
    } else {
      params.delete("entrada");
    }

    if (filters.dateRange?.to) {
      params.set("saida", toYmd(filters.dateRange.to));
    } else {
      params.delete("saida");
    }

    if (filters.service) {
      params.set("service", filters.service);
      params.delete("tipo_servico_id");
    }

    setSearchParams(params);
  };

  const listings = useMemo(() => {
    let final: ServiceCardItem[] = (items || []).map((it: any) => ({
      ...it,
      vagas: it.vagas
        ? { ...it.vagas, label: it.vagas?.label ?? "🏠 Hospeda até" }
        : null,
    }));

    switch (sortBy) {
      case "price-low":
        final.sort((a, b) => (a.preco_base ?? 0) - (b.preco_base ?? 0));
        break;
      case "rating":
        final.sort((a, b) => (b.avaliacoes?.media ?? 0) - (a.avaliacoes?.media ?? 0));
        break;
      default:
        break;
    }

    return final;
  }, [items, sortBy]);

  const totalResultados = total;
  const isLoading = loading;
  const displayedListings: ServiceCardItem[] = listings.slice(0, visibleCount);
  const hasMore = visibleCount < listings.length;
  const reviews = reviewsItem?.avaliacoes?.historico ?? [];
  const reviewsWithComment = reviews.filter((review) => review.tem_comentario || review.comentario);
  const reviewTotal = Number(reviewsItem?.avaliacoes?.total || 0);
  const reviewMedia = Number(reviewsItem?.avaliacoes?.media || 0);

  const resumoServico = service
    ? service.replace(/_/g, " ")
    : tipo_servico_id_legacy
    ? `serviço ${tipo_servico_id_legacy}`
    : "";

  const goDetail = (id: number) => {
    const qs = new URLSearchParams(searchParams.toString());

    if (!qs.get("entrada")) return;

    if (!qs.get("saida")) qs.set("saida", qs.get("entrada")!);

    navigate(`/servico/${id}?${qs.toString()}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header activeTab="tutores" isCompact />

      <div className="sticky top-[57px] md:top-[65px] z-40 bg-background border-b">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <div className="md:hidden mb-3 px-1">
            <SearchBar
              isCompact
              onSearch={handleSearch}
              cidades={cidades}
              servicos={serviceOptions}
              initialService={service}
              showServiceSelector
            />
          </div>

          <div className="hidden md:block mb-4">
            <SearchBar
              onSearch={handleSearch}
              cidades={cidades}
              servicos={serviceOptions}
              initialService={service}
              showServiceSelector
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
            <Link
              to="/"
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Voltar</span>
            </Link>

            <span className="text-muted-foreground">·</span>

            <span className="font-medium leading-tight">
              {isLoading
                ? "Buscando resultados..."
                : `${totalResultados} resultado${totalResultados !== 1 ? "s" : ""}`}
              {cidade && ` em ${cidade}`}
              {resumoServico && ` para ${resumoServico}`}
            </span>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 border-t">
          <FilterChips
            selectedFilters={selectedFilters}
            onFilterChange={setSelectedFilters}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        </div>
      </div>

      <main className="container mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="hidden lg:flex justify-end mb-4">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-md"
            >
              <List className="w-4 h-4 mr-1" />
              Lista
            </Button>

            <Button
              variant={viewMode === "map" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("map")}
              className="rounded-md"
            >
              <Map className="w-4 h-4 mr-1" />
              Mapa
            </Button>
          </div>
        </div>

        {err && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Erro ao carregar resultados: {err}
          </div>
        )}

        {viewMode === "grid" ? (
          <>
            {isLoading ? (
              <div className="py-10 text-center text-sm md:text-base text-muted-foreground">
                Carregando resultados...
              </div>
            ) : displayedListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {displayedListings.map((it) => {
                console.log("CARD DA BUSCA", it);
                console.log("HORARIOS", it.precos_detalhados);
                return (
                  <div
                    key={it.servico_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => goDetail(it.servico_id)}
                    onKeyDown={(e) => e.key === "Enter" && goDetail(it.servico_id)}
                    className="cursor-pointer active:scale-[0.98] transition"
                  >
                    <ServiceCard it={it} onReviewsClick={setReviewsItem} />
                  </div>
                );
              })}
              </div>
            ) : (
              <div className="py-10 text-center">
                <div className="text-base font-semibold text-foreground mb-2">
                  Nenhum resultado encontrado
                </div>
                <div className="text-sm text-muted-foreground">
                  Tente alterar cidade, datas ou tipo de serviço.
                </div>
              </div>
            )}

            {hasMore && !isLoading && (
              <div className="text-center mt-8 md:mt-10">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setVisibleCount((prev) => prev + 8)}
                  className="rounded-full px-6 md:px-8 w-full sm:w-auto"
                >
                  Mostrar mais ({listings.length - visibleCount} restantes)
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="h-[400px] md:h-[600px] bg-muted rounded-xl flex items-center justify-center">
            <div className="text-center px-4">
              <Map className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm md:text-base text-muted-foreground">
                Visualização de mapa disponível em breve
              </p>
            </div>
          </div>
        )}
      </main>

      <Dialog open={!!reviewsItem} onOpenChange={(open) => !open && setReviewsItem(null)}>
        <DialogContent className="max-h-[86vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Avaliações de {reviewsItem?.anfitriao?.nome || "anfitrião"}
            </DialogTitle>
            <DialogDescription>
              Histórico de feedbacks enviados por tutores após reservas concluídas.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl bg-forest-light p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Star className="h-6 w-6 fill-[#d4af37] text-[#d4af37]" />
                  <span className="text-3xl font-extrabold text-foreground">
                    {reviewMedia.toFixed(1)}
                  </span>
                </div>
                <p className="mt-1 text-sm font-semibold text-forest">
                  {reviewTotal} avaliação{reviewTotal === 1 ? "" : "es"} recebida{reviewTotal === 1 ? "" : "s"}
                </p>
              </div>
              <RatingStars value={reviewMedia} size="md" />
            </div>
          </div>

          {reviews.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                  <MessageSquareText className="h-4 w-4 text-forest" />
                  Comentários
                </span>
                <span>
                  {reviewsWithComment.length} comentário{reviewsWithComment.length === 1 ? "" : "s"}
                </span>
              </div>

              {reviews.map((review) => (
                <article key={review.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                          {(review.tutor_nome || "T").charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-foreground">
                          {review.tutor_nome || "Tutor"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {review.servico_nome || "Serviço"} {review.data ? `• ${review.data}` : ""}
                        </p>
                      </div>
                    </div>
                    <RatingStars value={review.nota} />
                  </div>

                  {review.comentario ? (
                    <p className="mt-4 text-sm leading-6 text-slate-700">
                      “{review.comentario}”
                    </p>
                  ) : (
                    <p className="mt-4 text-sm text-muted-foreground">
                      Avaliação sem comentário escrito.
                    </p>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl bg-slate-50 p-6 text-center">
              <p className="text-sm font-semibold text-slate-700">
                Este anfitrião ainda não recebeu avaliações.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Assim que tutores avaliarem reservas concluídas, o histórico aparecerá aqui.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
